import { useCallback, useEffect, useState } from "react";
import type { Editor } from "@tiptap/react";
import { FlagIcon } from "@phosphor-icons/react";
import { useFeatureFlag } from "@/zapi-frontend/shared/hooks/useFeatureFlag";
import { FlagDeficiencyDialog } from "./FlagDeficiencyDialog";

const TOOLBAR_OFFSET = 40;

type Props = {
  editor: Editor | null;
  caseBriefId: number;
  projectId?: number | null;
};

export function CaseBriefFlagAffordance({ editor, caseBriefId, projectId }: Props) {
  const isFlagEnabled = useFeatureFlag("case-brief-flag");
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const [quote, setQuote] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const syncToSelection = useCallback(() => {
    if (!editor || isDialogOpen) {
      setAnchor(null);
      return;
    }
    const { from, to, empty } = editor.state.selection;
    const selectedText = empty ? "" : editor.state.doc.textBetween(from, to, "\n");
    if (!selectedText.trim()) {
      setAnchor(null);
      return;
    }
    const start = editor.view.coordsAtPos(from);
    setQuote(selectedText);
    setAnchor({ top: Math.max(start.top - TOOLBAR_OFFSET, 8), left: Math.max(start.left, 8) });
  }, [editor, isDialogOpen]);

  useEffect(() => {
    if (!editor || !isFlagEnabled) return;
    editor.on("selectionUpdate", syncToSelection);
    editor.on("blur", syncToSelection);
    window.addEventListener("scroll", syncToSelection, true);
    window.addEventListener("resize", syncToSelection);
    return () => {
      editor.off("selectionUpdate", syncToSelection);
      editor.off("blur", syncToSelection);
      window.removeEventListener("scroll", syncToSelection, true);
      window.removeEventListener("resize", syncToSelection);
    };
  }, [editor, isFlagEnabled, syncToSelection]);

  if (!isFlagEnabled) return null;

  return (
    <>
      {anchor && (
        <div
          className="fixed z-[60] flex items-center gap-1 rounded-lg border border-innoveraZinc-150 bg-white px-1 py-1 shadow-lg"
          style={{ top: anchor.top, left: anchor.left }}
        >
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setIsDialogOpen(true)}
            className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[13px] text-innoveraZinc-700 hover:bg-innoveraZinc-50"
          >
            <FlagIcon className="h-4 w-4" />
            Flag
          </button>
        </div>
      )}

      <FlagDeficiencyDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        target={{ kind: "caseBrief", caseBriefId, projectId }}
        quote={quote}
      />
    </>
  );
}
